// Zoom slide (nothing snaps): when a zoom step regroups the pins, they slide to their new spots instead of jumping.
// Zooming in, a group's members and sub-groups slide out from the group's spot to their own; zooming out, they slide
// into the spot of the group they join (as Apple and Google Maps animate their clusters).
//
// How: MapLibre regroups when the grouped source's tiles for the new zoom arrive. The moment one does (before the map
// draws it), what's on screen is read, the map's own pins and groups are hidden, and this canvas over the map draws
// them instead. Once the map has placed the new groups, they're read too and each pin or group slides from where it
// was (or from the group it leaves) to where it now is; at the end the map's own pins show again in the same frame.
// The map's label fade stays on: its crossfade of the regrouped pins happens under the hidden layers, finished before
// they show (SLIDE_MS is longer than the map's fade).
//
// Cheap at any number of events: only what's on screen is read and drawn (one image per pin or group per frame), and
// matching a step's pins to their groups is done once per step, by the on-screen counts. Reduced motion: no slide;
// the map swaps in one frame (EventMap turns its fade off too).
import * as maplibregl from "maplibre-gl";
import { CLUSTER_RADIUS, GROUP_LAYER } from "./layers";
import type { MapController } from "./MapController";

/** How long a slide takes (ms). Longer than MapLibre's symbol fade (300 ms), which finishes under the hidden layers. */
export const SLIDE_MS = 400;
/** Waiting for the map to place a new tile's groups gives up after this long (ms; nothing regrouped). */
const WAIT_MS = 1000;
/** However late tiles keep coming, the map's own pins show again after this long (ms). */
const MAX_HIDDEN_MS = 2500;
export const SLIDE_LAYERS = [GROUP_LAYER, "primary-pins"] as const;
export const reducedMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

type LngLat = [number, number];
interface Item { key: string; group: boolean; count: number; icon: string; opacity: number; at: LngLat }
/** One pin or group drawn during a slide: from → to, its opacity going a0 → a1 between t0 and t1 (of the slide). */
interface Sprite { item: Item; from: LngLat; to: LngLat; a0: number; a1: number; t0: number; t1: number; layer: number }

/** The grouped source's tiles for the map's zoom (512 px tiles, up to the source's max zoom 18). */
const tileZoom = (map: maplibregl.Map) => Math.min(18, Math.floor(map.getZoom()));
const ease = (t: number) => 1 - (1 - t) ** 3;
const lerp = (a: LngLat, b: LngLat, k: number): LngLat => [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];

export class ZoomSlide {
  private readonly canvas = document.createElement("canvas");
  private readonly ctx = this.canvas.getContext("2d")!;
  private sprites: Sprite[] = [];
  private start = 0;
  /** Waiting for the map to place a new tile's groups: what was on screen, and how many frames so far. */
  private waiting: { before: Map<string, Item & { now: LngLat }>; since: number } | null = null;
  /** The step: +1 zooming in, -1 out; and the coarser of its two grouping zooms. */
  private dir = 0;
  private coarse = 0;
  /** What the slide ends on, and how many more frames to look for pins the map placed late. */
  private target = new Set<string>();
  private checks = 0;
  /** The groups the last slide's pins came out of (a pin placed late comes out of them too). */
  private parents: (Item & { now: LngLat })[] = [];
  private hidden = false;
  private hiddenAt = 0;
  /** When the zoom last crossed a level. */
  private steppedAt = 0;
  /** The map's own pins were shown again last frame; this canvas clears once the map has drawn them. */
  private clearNext = false;
  /** From the first zoom step until the map settles, every new tile of the grouped source is a step. */
  private stepping = false;
  /** The grouped source's tile zoom (its groups are worked out per whole zoom level). */
  private lastZ: number | null;
  private images = new Map<string, CanvasImageSource>();
  /** For tests: every frame drawn, the number of groups showing and the sprites drawn. */
  frames: { groups: number; drawn: number; at: Record<string, [number, number, number]> }[] | null = null;

  constructor(private readonly ctl: MapController) {
    const map = ctl.map;
    this.lastZ = tileZoom(map);
    this.canvas.className = "nowish-slide";
    // Above the map's canvas, under its markers (the selected pin, a popping group's pins).
    map.getCanvasContainer().insertBefore(this.canvas, map.getCanvas().nextSibling);
    map.on("sourcedata", (e: maplibregl.MapSourceDataEvent & { tile?: { tileID: { overscaledZ: number } } }) => {
      if (e.sourceId !== "primary" || !e.tile) return;
      // A tile for the step's zoom arriving (not the coarser ones the map loads to fill gaps while it waits).
      if (!this.stepping || e.tile.tileID.overscaledZ !== this.lastZ) return;
      // Mid-slide, its groups join the running slide (looked for over the next frames); after it, a new step.
      if (this.hidden && !this.waiting) this.checks = Math.max(this.checks, 10);
      else if (!this.hidden && performance.now() - this.steppedAt < WAIT_MS) this.step();
    });
    // The step itself: the zoom crossing a whole level, before the map draws it (it may have that level's tiles
    // already, from an earlier visit, and then regroups in this very frame).
    map.on("zoom", () => {
      const z = tileZoom(map);
      if (this.lastZ !== null && z !== this.lastZ) { this.dir = Math.sign(z - this.lastZ); this.coarse = Math.min(z, this.lastZ); this.lastZ = z; this.steppedAt = performance.now(); this.step(); }
      this.lastZ = z;
    });
    map.on("render", () => this.onRender());
    map.on("idle", () => { this.stepping = false; });
  }

  get active() { return this.hidden; }

  /** A tile of the grouped source for another zoom arrived: hold what's on screen and wait for the regrouped pins. */
  private step() {
    if (reducedMotion()) return;
    this.stepping = true;
    const map = this.ctl.map, before = this.now();
    if (!before.size && !this.hidden) return;
    if (!this.waiting) this.waiting = { before, since: performance.now() };
    else this.waiting.before = before;
    this.sprites = [...before.values()].map((b) => ({ item: b, from: b.now, to: b.now, a0: 1, a1: 1, t0: 0, t1: 1, layer: 0 }));
    this.start = performance.now();
    this.hide(true);
    map.triggerRepaint();
  }

  /** What's on screen now: mid-slide, where each is drawn (the ones fading out are let go); else the map's own. */
  private now() {
    const before = new Map<string, Item & { now: LngLat }>(), t = this.progress();
    if (this.hidden) {
      for (const s of this.sprites) {
        if (s.a1 === 0 && s.t1 <= 1) continue;
        before.set(s.item.key, { ...s.item, now: lerp(s.from, s.to, ease(t)) });
      }
    } else {
      for (const it of this.read()) before.set(it.key, { ...it, now: it.at });
    }
    return before;
  }

  private progress() { return this.hidden ? Math.min(1, (performance.now() - this.start) / SLIDE_MS) : 1; }

  /** The map's pins and groups on screen now. */
  private read(): Item[] {
    const seen = new Map<string, Item>();
    for (const f of this.ctl.map.queryRenderedFeatures({ layers: [...SLIDE_LAYERS] })) {
      const p = f.properties ?? {}, group = f.layer.id === GROUP_LAYER;
      const key = group ? `g|${p.cluster_id}` : `p|${p.id}`;
      if (seen.has(key)) continue;
      const at = (f.geometry as GeoJSON.Point).coordinates as LngLat;
      seen.set(key, {
        key, group, count: group ? Number(p.point_count) : 1, at: [at[0], at[1]], opacity: group ? 1 : Number(p.opacity ?? 1),
        icon: group ? ["group", p.cluster_id, p.point_count, p.tone, p.best, p.free].join("|") : String(p.icon),
      });
    }
    return [...seen.values()];
  }

  private onRender() {
    if (this.clearNext) { this.clearNext = false; this.clear(); }
    if (!this.hidden) return;
    if (performance.now() - this.hiddenAt > MAX_HIDDEN_MS) { this.waiting = null; this.checks = 0; this.finish(); return; }
    const w = this.waiting;
    if (w) {
      const after = this.read();
      // Regrouped once something new is placed (the old tile's pins can go a frame before the new ones come).
      const changed = after.some((a) => !w.before.has(a.key));
      if (changed) { this.slide(w.before, after); this.checks = 10; }
      else if (performance.now() - w.since > WAIT_MS) { this.waiting = null; this.finish(); return; }
    } else if (this.checks > 0) {
      // The map can place a tile's groups over a few frames: any it placed late join the slide from where things are.
      this.checks--;
      const after = this.read();
      if (after.some((a) => !this.target.has(a.key))) this.slide(this.now(), after, this.parents);
    }
    this.draw();
    if (!this.waiting && this.progress() >= 1) this.finish();
    else this.ctl.map.triggerRepaint();
  }

  /** Pairs what was on screen with what is now, and sets each one's slide. */
  private slide(before: Map<string, Item & { now: LngLat }>, after: Item[], parents: (Item & { now: LngLat })[] = []) {
    const map = this.ctl.map, dir = this.dir;
    this.waiting = null;
    this.target = new Set(after.map((a) => a.key));
    // How far a member can be from its group: the grouping radius at the coarser of the two zooms, in pixels now.
    const reach = 1.5 * CLUSTER_RADIUS * 2 ** (map.getZoom() - this.coarse);
    const px = (ll: LngLat) => map.project(ll);
    const nearest = <T extends { at: LngLat }>(from: LngLat, list: T[]) => {
      const p = px(from);
      let best: T | null = null, bd = reach;
      for (const c of list) { const q = px(c.at), d = Math.hypot(q.x - p.x, q.y - p.y); if (d < bd) { bd = d; best = c; } }
      return best;
    };
    const sprites: Sprite[] = [];
    const gone = [...before.values()].filter((b) => !this.target.has(b.key)).map((b) => ({ ...b, at: b.now }));
    const fresh = after.filter((a) => !before.has(a.key));
    const groupsGone = [...gone.filter((g) => g.group), ...parents.filter((p) => !before.has(p.key) && !this.target.has(p.key))];
    this.parents = groupsGone;
    for (const a of after) {
      const b = before.get(a.key);
      if (b) sprites.push({ item: a, from: b.now, to: a.at, a0: 1, a1: 1, t0: 0, t1: 1, layer: 0 });
    }
    if (dir > 0) {
      // Zooming in: each new pin or group slides out from the nearest group that went; that group fades as they leave
      // (or, when it only moved and kept all its members, is simply replaced by its moving self).
      const kids = new Map<string, Item[]>();
      for (const a of fresh) {
        const parent = nearest(a.at, groupsGone);
        if (parent) { kids.set(parent.key, [...(kids.get(parent.key) ?? []), a]); sprites.push({ item: a, from: parent.at, to: a.at, a0: 1, a1: 1, t0: 0, t1: 1, layer: 1 }); }
        else sprites.push({ item: a, from: a.at, to: a.at, a0: 0, a1: 1, t0: 0, t1: 1, layer: 1 });
      }
      for (const g of gone) {
        const k = kids.get(g.key);
        if (k && k.length === 1 && k[0]!.group && k[0]!.count === g.count) continue;
        sprites.push(k ? { item: g, from: g.at, to: g.at, a0: 1, a1: 0, t0: 0, t1: 0.15, layer: 2 } : { item: g, from: g.at, to: g.at, a0: 1, a1: 0, t0: 0, t1: 0.5, layer: 0 });
      }
    } else {
      // Zooming out (or the same zoom): each pin or group that went slides into the nearest new group; the group fades
      // in as they arrive (or, when it's one group that only moved, takes over from it at the end).
      const into = new Map<string, Item[]>();
      for (const g of gone) {
        const target = dir < 0 ? nearest(g.at, fresh.filter((a) => a.group)) : null;
        if (target) { into.set(target.key, [...(into.get(target.key) ?? []), g]); sprites.push({ item: g, from: g.at, to: target.at, a0: 1, a1: 0, t0: 0.85, t1: 1.001, layer: 1 }); }
        else sprites.push({ item: g, from: g.at, to: g.at, a0: 1, a1: 0, t0: 0, t1: 0.5, layer: 0 });
      }
      for (const a of fresh) {
        const k = into.get(a.key);
        if (k && k.length === 1 && k[0]!.group && k[0]!.count === a.count) {
          // The one group, moved: it draws itself the whole way (the sprite above, kept at full strength).
          const s = sprites.find((x) => x.item.key === k[0]!.key)!; s.a1 = 1; s.t1 = 1; continue;
        }
        sprites.push(k ? { item: a, from: a.at, to: a.at, a0: 0, a1: 1, t0: 0.85, t1: 1, layer: 2 } : { item: a, from: a.at, to: a.at, a0: 0, a1: 1, t0: 0, t1: 1, layer: 0 });
      }
    }
    this.sprites = sprites.sort((x, y) => x.layer - y.layer);
    this.start = performance.now();
  }

  private draw() {
    const map = this.ctl.map, c = this.canvas, dpr = devicePixelRatio || 1;
    const w = map.getCanvas().clientWidth, h = map.getCanvas().clientHeight;
    if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
      c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); c.style.width = `${w}px`; c.style.height = `${h}px`;
    }
    const ctx = this.ctx, t = this.progress(), k = ease(t);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    let groups = 0, drawn = 0;
    const at: Record<string, [number, number, number]> = {};
    for (const s of this.sprites) {
      const a = s.a0 + (s.a1 - s.a0) * Math.min(1, Math.max(0, (t - s.t0) / (s.t1 - s.t0)));
      const alpha = a * s.item.opacity;
      if (alpha <= 0.01) continue;
      const img = this.image(s.item.icon);
      if (!img) continue;
      const p = map.project(lerp(s.from, s.to, k)), iw = img.width / 2, ih = img.height / 2;
      if (p.x < -iw || p.y < -ih || p.x > w + iw || p.y > h + ih) continue;
      ctx.globalAlpha = alpha;
      ctx.drawImage(img, p.x - iw / 2, p.y - ih / 2, iw, ih);
      drawn++;
      if (this.frames) at[s.item.key] = [Math.round(p.x), Math.round(p.y), Math.round(alpha * 100) / 100];
      if (s.item.group && a > 0.5) groups++;
    }
    ctx.globalAlpha = 1;
    this.frames?.push({ groups, drawn, at });
  }

  /** A pin's or group's image as the map has it (painted at 2×), copied once to a canvas this one can draw. */
  private image(id: string): (CanvasImageSource & { width: number; height: number }) | null {
    let img = this.images.get(id) as (HTMLCanvasElement | undefined);
    if (img) return img;
    const si = this.ctl.map.getImage(id) as unknown as { data?: { width: number; height: number; data: Uint8Array | Uint8ClampedArray } } | null;
    if (!si?.data) return null;
    const { width, height, data } = si.data;
    img = document.createElement("canvas"); img.width = width; img.height = height;
    img.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(data), width, height), 0, 0);
    if (this.images.size > 400) this.images.clear();
    this.images.set(id, img);
    return img;
  }

  /** The slide is over: the map's own pins show again (they're already where the slide ended). This canvas keeps its
   *  last frame until the map has drawn them, so there's no frame with neither. */
  private finish() {
    this.sprites = [];
    this.hide(false);
    this.clearNext = true;
    this.ctl.map.triggerRepaint();
  }
  private clear() { this.ctx.setTransform(1, 0, 0, 1, 0, 0); this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height); this.images.clear(); }
  private hide(on: boolean) {
    if (this.hidden === on) return;
    this.hidden = on;
    if (on) this.hiddenAt = performance.now();
    this.ctl.slideHides(on);
  }
  destroy() { this.canvas.remove(); }
}
